import { Form, FormProps, Input, Select } from "antd";
import { useEffect } from "react";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import {
  createUserAsync,
  updateUserDataAsync,
} from "../../../store/features/csoManager/user/userApi";

import { getAllRoleListAsync } from "../../../store/features/csoManager/role/roleApi";
import { getAllRegionalHubAsync } from "../../../store/features/geo/createGeo/regionApi";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { ModalType } from "../../../types";
import { UpdateUserData } from "../../../types/csoManager/user";
import { ArrayOption, selectSearchOption } from "../../../utils/utils";
import FormModal from "../../ui/FormModal";

type Props = {
  open: ModalType;
  handleClose: () => void;
};

export default function AddCso({ open, handleClose }: Props) {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();

  const { manageLoading, roleList } = useAppSelector((state) => state.role);
  const { regionHubList } = useAppSelector((state) => state.regionalHub);
  const { singleUserData } = useAppSelector((state) => state.user);

  const csoUserData = open?.data as UpdateUserData;
  const onFinish: FormProps<any>["onFinish"] = (values) => {
    if (open?.type === "ADD") {
      dispatch(
        createUserAsync({
          data: values,
          others: {
            reRender: handleClose,
          },
        })
      );
    } else {
      const updatedData = {
        email: values.email,
        fullName: values.fullName,
        mobile: values.mobile,
        regional_hub_id: values.regional_hub_id,
        role_id: values.role_id,
      };

      dispatch(
        updateUserDataAsync({
          data: updatedData,
          others: {
            reRender: handleClose,
            id: singleUserData?._id,
          },
        })
      );
    }
  };

  // add value in modal for view and edit
  useEffect(() => {
    if (
      open?.type === "EDIT" &&
      singleUserData?._id &&
      manageLoading === false
    ) {
      form.setFieldsValue({
        fullName: singleUserData?.fullName,
        email: singleUserData?.email,
        mobile: singleUserData?.mobile,
        regional_hub_id: singleUserData?.regional_hub_id?._id,
        role_id: singleUserData?.role_id?._id,
        userStatus: singleUserData?.userStatus || "Active",
      });
    }
  }, [
    singleUserData?._id,
    singleUserData?.email,
    singleUserData?.fullName,
    singleUserData?.mobile,
    singleUserData?.regional_hub_id,
    singleUserData?.role_id?._id,
    singleUserData?.userStatus,
    form,
    manageLoading,
    open?.type,
  ]);

  // get district
  useEffect(() => {
    dispatch(
      getAllRegionalHubAsync({
        params: {
          skip: 0,
          limit: 0,
          division_id: "",
          district_id: "",
          hubTitle: "",
          status: "",
        },
      })
    );
  }, [dispatch]);

  // Fetch roles on component mount
  useEffect(() => {
    dispatch(
      getAllRoleListAsync({
        params: {
          skip: 0,
          limit: 0,
          status: "",
        },
      })
    );
  }, [dispatch]);

  return (
    <FormModal
      disabled={false}
      open={open?.open}
      isUpdate={open?.type === "EDIT"}
      title={`${open?.type === "EDIT" ? "Update" : "Create"} a CSO`}
      onFinish={onFinish}
      handleClose={handleClose}
      buttonText={open?.type === "EDIT" ? "Update" : "Create"}
      formProps={{
        form,
      }}
    >
      <Form.Item
        name="fullName"
        label="Full Name"
        rules={[
          {
            required: true,
            message: "Please enter full name",
          },
        ]}
      >
        <Input
          size="large"
          placeholder="Enter full name"
          disabled={open?.type === "VIEW"}
        />
      </Form.Item>

      <Form.Item
        name="email"
        label="User Email"
        rules={[
          {
            required: true,
            message: "Please enter your email!",
          },
          {
            // Stricter email validation pattern
            pattern:
              /^[a-zA-Z0-9]+([._-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+([.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/,
            message: "Please enter a valid email address!",
          },
        ]}
      >
        <Input
          size="large"
          placeholder="Enter a valid email address"
          disabled={open?.type === "VIEW"}
        />
      </Form.Item>

      <Form.Item
        name="mobile"
        label="Mobile No."
        rules={[
          {
            required: true,
            message: "Please enter 11 digits mobile number",
          },
          {
            pattern: /^\d{11}$/,
            message: "Mobile number must be exactly 11 digits",
          },
        ]}
      >
        <Input
          prefix="+88"
          size="large"
          placeholder="Enter 11 digits mobile number"
          disabled={open?.type === "VIEW"}
        />
      </Form.Item>

      {open?.type !== "EDIT" ? (
        <Form.Item
          name="password"
          label="Password"
          rules={[
            {
              required: true,
              message: "Please enter password",
            },
            {
              min: 8,
              message: "Password must be at least 8 characters long",
            },
          ]}
        >
          <Input
            size="large"
            placeholder="********"
            disabled={open?.type === "VIEW"}
          />
        </Form.Item>
      ) : null}

      <Form.Item
        name="regional_hub_id"
        label="Region"
        rules={[
          {
            required: true,
            message: "Please select region",
          },
        ]}
      >
        <Select
          placeholder="Select"
          size="large"
          allowClear
          showSearch
          filterOption={selectSearchOption}
          options={ArrayOption(regionHubList, "hubTitle", "_id")}
          suffixIcon={<SelectDownIcon />}
          disabled={open?.type === "VIEW"}
        />
      </Form.Item>

      <Form.Item
        name="role_id"
        label="Role"
        rules={[
          {
            required: true,
            message: "Please select role",
          },
        ]}
      >
        <Select
          placeholder="Select Role"
          size="large"
          allowClear
          showSearch
          filterOption={selectSearchOption}
          options={ArrayOption(roleList, "role_name", "_id")}
          suffixIcon={<SelectDownIcon />}
          disabled={open?.type === "VIEW"}
        />
      </Form.Item>
    </FormModal>
  );
}
